import AdminController from "../../controllers/AdminController";
import { useQuery } from "react-query";
import { useEffect, useMemo, useState } from "react";
import {
  PolicyInsightsParams,
  UsageComparisonParams,
  UsageIterationsParams,
  UsageOverviewParams,
  UsageTimeseriesParams,
} from "../admin/types";

export const useAdminGetAgencies = (agencyId?: string | null) => {
  return useQuery({
    queryKey: ["admin-agencies", agencyId],
    queryFn: () => AdminController.getAgencies(agencyId),
  });
};

/**
 * Agencies for list and picker UIs: only those on the onboarding allowlist
 * (`active === true`), unless `includeUnlisted` is set.
 *
 * The ~200 prod records with no `active` field are overwhelmingly leftover test
 * data and unnamed self-serve signups. Filtering here keeps that rule in one
 * place so the components just render what they are handed.
 *
 * AgencyPage deliberately uses `useAdminGetAgencies` instead: a record that is
 * not on the allowlist must still be openable and editable, since that is the
 * page you would visit to put it on the list.
 */
export const useAdminGetListedAgencies = (includeUnlisted = false) => {
  const query = useAdminGetAgencies();
  const data = useMemo(
    () =>
      includeUnlisted
        ? query.data
        : query.data?.filter((agency) => agency.isListed),
    [query.data, includeUnlisted]
  );
  return { ...query, data };
};

export const useAdminSearchUsers = (query: string) => {
  return useQuery({
    queryKey: ["admin-search-users", query],
    queryFn: () => AdminController.searchUsers(query),
    enabled: query.length >= 2,
    staleTime: 60 * 1000,
    keepPreviousData: true,
    retry: false,
  });
};

export const useAdminGetAgencyRuns = (agencyId: string) => {
  return useQuery({
    queryKey: ["admin-agency-runs", agencyId],
    queryFn: () => AdminController.getAgencyRuns(agencyId),
    enabled: !!agencyId,
  });
};

export const useAdminRunningWorkflows = (startDate?: string | null, endDate?: string | null) => {
  return useQuery({
    queryKey: ["admin-running-workflows", startDate, endDate],
    queryFn: () => AdminController.getRunningWorkflows(startDate, endDate),
    refetchInterval: 60 * 1000, // refresh every 60s
  });
};

export const useAdminSalesMetrics = (
  startDate?: string,
  endDate?: string,
  rep?: string
) => {
  return useQuery({
    queryKey: ["admin-sales-metrics", startDate, endDate, rep],
    queryFn: () => AdminController.getSalesMetrics(startDate, endDate, rep),
    enabled: !!startDate && !!endDate,
  });
};

export const useAdminSalesReps = () => {
  return useQuery({
    queryKey: ["admin-sales-reps"],
    queryFn: () => AdminController.getSalesReps(),
  });
};

export const useAdminIssueCategories = () => {
  return useQuery({
    queryKey: ["admin-issue-categories"],
    queryFn: () => AdminController.getIssueCategories(),
  });
};

export const useAdminIssueCategoriesFiltered = (startDate?: string, endDate?: string) => {
  return useQuery({
    queryKey: ["admin-issue-categories-filtered", startDate, endDate],
    queryFn: () => AdminController.getIssueCategories(startDate, endDate),
    enabled: !!startDate && !!endDate,
  });
};

export const useAdminPrDashboard = () => {
  return useQuery({
    queryKey: ["admin-pr-dashboard"],
    queryFn: () => AdminController.getPrDashboard(),
    refetchInterval: 30 * 1000,
  });
};

export const useAdminCategoryHistory = () => {
  return useQuery({
    queryKey: ["admin-category-history"],
    queryFn: () => AdminController.getCategoryHistory(),
  });
};

export const useAdminUsageTimeseries = (
  params: UsageTimeseriesParams | null
) => {
  return useQuery({
    queryKey: [
      "admin-usage-timeseries",
      params?.startDate,
      params?.endDate,
      params?.granularity,
      params?.agencyId ?? null,
      params?.groupBy ?? null,
      params?.includeTestAccounts ?? false,
    ],
    queryFn: () => AdminController.getUsageTimeseries(params as UsageTimeseriesParams),
    enabled: !!params && !!params.startDate && !!params.endDate,
    keepPreviousData: true,
  });
};

export const useAdminUsageOverview = (params: UsageOverviewParams | null) => {
  return useQuery({
    queryKey: [
      "admin-usage-overview",
      params?.startDate,
      params?.endDate,
      params?.includeTestAccounts ?? false,
    ],
    queryFn: () => AdminController.getUsageOverview(params as UsageOverviewParams),
    enabled: !!params && !!params.startDate && !!params.endDate,
    keepPreviousData: true,
  });
};

export const useAdminUsageIterations = (params: UsageIterationsParams | null) => {
  return useQuery({
    queryKey: [
      "admin-usage-iterations",
      params?.startDate,
      params?.endDate,
      params?.agencyId ?? null,
      params?.includeTestAccounts ?? false,
    ],
    queryFn: () => AdminController.getUsageIterations(params as UsageIterationsParams),
    enabled: !!params && !!params.startDate && !!params.endDate,
    keepPreviousData: true,
  });
};

export const useAdminPolicyInsights = (params: PolicyInsightsParams | null) => {
  return useQuery({
    queryKey: [
      "admin-policy-insights",
      params?.startDate,
      params?.endDate,
      params?.agencyId ?? null,
      params?.includeTestAccounts ?? false,
    ],
    queryFn: () => AdminController.getPolicyInsights(params as PolicyInsightsParams),
    enabled: !!params && !!params.startDate && !!params.endDate,
    keepPreviousData: true,
  });
};

export const useAdminUsageComparison = (
  params: UsageComparisonParams | null
) => {
  return useQuery({
    queryKey: [
      "admin-usage-comparison",
      params?.periodAStart,
      params?.periodAEnd,
      params?.periodBStart,
      params?.periodBEnd,
      params?.agencyId ?? null,
      params?.groupBy ?? null,
      params?.includeTestAccounts ?? false,
    ],
    queryFn: () => AdminController.getUsageComparison(params as UsageComparisonParams),
    enabled:
      !!params &&
      !!params.periodAStart &&
      !!params.periodAEnd &&
      !!params.periodBStart &&
      !!params.periodBEnd,
    keepPreviousData: true,
  });
};
